import { useRelatedProducts } from "@api/products/products";
import Loader from "@components/atoms/Loader";
import { numberToMoneyString } from "@utils/numbers.util";
import Link from "next/link";
import RelatedItemOnModal from "@components/features/product-detail/related-product/RelatedItemOnModal";
import { useCartProducts } from "@api/customer/cart";
import { RECOMMEND_CATEGORY_ID as ROOT_CATEGORY_ID } from "@const/recommend-category.const";
import { useState, useEffect } from "react";
import {
  isWebview,
  clickCartButtonNative,
  hideMenuBarNative,
} from "@utils/webview.util";
import { PRODUCT_NOT_FOUND_IMG } from "@const/products.const";

interface Props {
  onClose: () => void;
  price: number;
  specialPrice: number;
  productTitle: string;
  sku: string;
  qty: number;
  imageUrl: string | null;
}

const AddToCartModal = ({
  onClose,
  price,
  specialPrice,
  productTitle,
  sku,
  qty,
  imageUrl,
}: Props) => {
  // get RelatedProduct
  const UPSELL_AND_RECOMMEND_PRODUCT_PAGE_SIZE = 5;
  const [randomPage, setRandomPage] = useState(1);

  function randomPageInRange(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  useEffect(() => {
    setRandomPage(randomPageInRange(1, 120));
    return () => {};
  }, []);

  const {
    data: dataRelatedProducts,
    isLoading: isLoadingRelatedProduct,
    error: errorRelatedProduct,
  } = useRelatedProducts({
    categoryId: ROOT_CATEGORY_ID,
    productDisplayConfig: {
      currentPage: randomPage,
      pageSize: UPSELL_AND_RECOMMEND_PRODUCT_PAGE_SIZE,
    }, // TODO: update static to dynamic values
  });

  const { isLoading, mutate: MutateCartProducts } = useCartProducts();
  if (sku && qty && !isLoading) {
    MutateCartProducts();
  }

  useEffect(() => {
    if (isWebview()) {
      hideMenuBarNative();
    }
  }, []);

  return (
    <>
      <div
        className="modal modal-cart"
        id="cartModal"
        tabIndex={-1}
        aria-labelledby='cartModalLabel"'
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content pb-md-3 rounded-2">
            <div className="modal-header pt-2 pb-1">
              <h5 className="modal-title mt-3">
                <i className="icon icon-success" />
                สินค้าจำนวน {qty} รายการได้ถูกเพิ่มลงรถเข็นแล้ว
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-12 col-lg-7 pe-md-4 border-right-md-1">
                  <div className="d-flex align-items-md-center">
                    <div className="img me-3 mb-md-2">
                      <img
                        src={imageUrl ? imageUrl : PRODUCT_NOT_FOUND_IMG}
                        alt={productTitle}
                      />
                    </div>
                    <div style={{ width: "100%" }}>
                      <p className="text-lineclamp mb-1">{productTitle}</p>
                      <small className="d-block mt-2">SKU : {sku}</small>
                      <div className="d-flex justify-content-between mt-2">
                        <p className="text-red mb-0">
                          <b>
                            {numberToMoneyString(
                              specialPrice > 0 && specialPrice < price
                                ? specialPrice
                                : price,
                              0
                            )}
                            .-
                          </b>
                        </p>
                        <small>จำนวน : {qty}</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-5 ps-md-4">
                  <hr className="my-3 d-lg-none" />
                  <p className="big">
                    รถเข็น <small className="text-muted">( 1 ชิ้น )</small>
                  </p>
                  <div className="d-flex justify-content-between text-red">
                    <p>ยอดรวม</p>
                    <p>
                      <b>
                        {numberToMoneyString(
                          specialPrice > 0 && specialPrice < price
                            ? specialPrice
                            : price,
                          0
                        )}
                        .-
                      </b>
                    </p>
                  </div>
                  <div className="d-flex justify-content-between text-red">
                    <Link href={`/customer/cart/products`}>
                      <a
                        className="btn btn-outline-danger flex-fill me-2"
                        tabIndex={-1}
                        role="button"
                        aria-disabled="true"
                        onClick={(e) => {
                          if (isWebview()) {
                            e.preventDefault();
                            clickCartButtonNative();
                          }
                        }}
                      >
                        ไปยังรถเข็น
                      </a>
                    </Link>
                    <Link href={`/customer/cart/checkout`}>
                      <a
                        href="#"
                        className={`btn btn-red flex-fill ms-2 ${
                          isLoading && "disabled"
                        }`}
                        tabIndex={-1}
                        role="button"
                        aria-disabled="true"
                      >
                        ดำเนินการชำระเงิน
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
              <hr className="my-4" />
              <div className="product-section mt-2">
                <div className="row">
                  <div className="col-12">
                    <h4>สินค้าแนะนำสำหรับคุณ</h4>
                  </div>
                </div>
                <div className="product-list-grid product-col-lg-5">
                  <div className="row">
                    {isLoadingRelatedProduct ? (
                      <Loader />
                    ) : (
                      dataRelatedProducts?.items && (
                        <RelatedItemOnModal
                          dataRelated={dataRelatedProducts!.items}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default AddToCartModal;
